import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { Braces, ChevronDown } from 'lucide-react';
import { useTemplateMergeFields } from './useTemplateMergeFields';
import styles from './MergeFieldDropdown.module.css';

interface MergeFieldDropdownProps {
  onInsert: (token: string) => void;
  compact?: boolean;
  className?: string;
  dropdownAlign?: 'left' | 'right';
}

export const MergeFieldDropdown: React.FC<MergeFieldDropdownProps> = ({
  onInsert,
  compact = false,
  className,
  dropdownAlign = 'left',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { mergeFields } = useTemplateMergeFields();

  const filteredGroups = mergeFields
    .map(group => ({
      ...group,
      entries: group.entries.filter(entry => entry.label.toLowerCase().includes(search.toLowerCase())),
    }))
    .filter(group => group.entries.length > 0);

  const close = () => {
    setIsOpen(false);
    setSearch('');
  };

  const handleToggle = () => {
    if (!isOpen) {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        const style: React.CSSProperties = { position: 'fixed', top: rect.bottom + 8, zIndex: 9999 };
        if (dropdownAlign === 'right') {
          style.right = window.innerWidth - rect.right;
        } else {
          style.left = rect.left;
        }
        setPortalStyle(style);
      }
    }
    setIsOpen(prev => !prev);
  };

  const handleSelect = (token: string) => {
    onInsert(token);
    close();
  };

  const triggerClass = compact ? styles.triggerCompact : styles.trigger;

  return (
    <div className={clsx(styles.wrapper, className)}>
      <button ref={triggerRef} type="button" className={triggerClass} onClick={handleToggle}>
        <Braces />
        {!compact && 'Insert merge field'}
        {!compact && <ChevronDown />}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className={styles.overlay} onClick={close} />
            <div className={styles.dropdownPortal} style={portalStyle}>
              <input
                type="text"
                placeholder="Search fields..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={styles.search}
                autoFocus
              />
              <div className={styles.options}>
                {filteredGroups.map(group => (
                  <div key={group.category}>
                    <p className={styles.groupLabel}>{group.category.toUpperCase()}</p>
                    {group.entries.map(entry => (
                      <button
                        key={entry.value}
                        type="button"
                        className={styles.option}
                        onClick={() => handleSelect(entry.value)}>
                        <span>{entry.label}</span>
                        <code>{entry.value}</code>
                      </button>
                    ))}
                  </div>
                ))}
                {filteredGroups.length === 0 && <p className={styles.empty}>No fields match</p>}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
